import * as React from 'react'
import { useCallback, useEffect, useMemo } from 'react'
import { useMediaQuery } from 'react-responsive'

import dayjs from 'dayjs'
import { useRouter } from 'next/router'

import { getInitialCheckoutPath } from '~/utils/checkoutFlow'
import { dayjsToString } from '~/utils/graphqlDataFormatters'
import { BackgroundWrapper, mediaQueryIsMobile, mediaQueryIsTablet, UniversalContainer } from '~/utils/grid'
import { runEcommerceDetails } from '~/utils/gtag'
import { isDaily, isFER } from '~/utils/isReservableType'
import { RESERVABLES_SEARCH_DAYS_COUNT } from '~/utils/reservables'

import { ProductContainer } from '~/components/ChefsMenu/ChefsMenuPage/ChefsMenuPageShards'
import { slotSelectModalProps } from '~/components/ChefsMenu/ChefsMenuPage/ChefsMenuPageUtils'
import FestivalBanner from '~/components/Festival/FestivalBanner'
import Contact from '~/components/Product/ProductDetailsPanel/Contact/ContactView'
import Description from '~/components/Product/ProductDetailsPanel/Description/DescriptionView'
import Features from '~/components/Product/ProductDetailsPanel/Features/FeaturesView'
import { ProductPageSection } from '~/components/Product/ProductPageSection/ProductPageSection'
import { ProductPageSectionSeparator, Section } from '~/components/Product/ProductPageSection/ProductPageSectionShards'
import SlotSelectionSkeleton from '~/components/Reservation/SlotSelectionPanel/SlotSelectionSkeleton'
import { MenuTabView } from '~/components/Restaurant/MenuTab/MenuTabView'
import { useRestaurantPageTabs } from '~/components/Restaurant/RestaurantPage/RestaurantPageUtils'
import RestaurantReservablesView from '~/components/Restaurant/RestaurantReservables/RestaurantReservablesView'
import { RESERVABLE_TYPES } from '~/constants'
import {
  CreateDailyReservationMutation,
  CreateFestivalReservationMutation,
  SingleRestaurantQuery,
  usePageDetailQuery,
  useReservablesQuery,
  useSingleRestaurantQuery,
} from '~/generated/graphql'
import { gt } from '~/locale'
import { NextPageWithLayout } from '~/pages/_app'
import Button from '~/shared/atoms/Button'
import { useModal } from '~/shared/molecules/Modal'
import ProductPageTitle from '~/shared/molecules/ProductPageTitle'
import ScrollableTabs from '~/shared/molecules/ScrollableTabs'

import NoSlotsBoard from '../NoSlotsBoard'

import { getRestaurantPageTabs } from './constants'
import { RestaurantPageCarousel } from './RestaurantPageCarousel'
import {
  ContentContainer,
  FestivalBannerContainer,
  HeaderContainer,
  ModalContainer,
  ModalDescription,
  ModalInnerContainer,
  OrSeparator,
  ProductGrid,
  RestaurantSelectContainer,
  SelectSeparator,
  SlotsContainer,
  SlotsHeading,
  SlotsTitle,
  StyledToastContainer,
  TabsWrapper,
} from './RestaurantPageShards'

export interface RestaurantPageProps {
  restaurant: NonNullable<SingleRestaurantQuery['restaurant']>
  restaurantId: string
  // similarRestaurants: RestaurantListElementFragment[]
  defaultCollapse?: string
}

const RestaurantPage: NextPageWithLayout<RestaurantPageProps> = ({ restaurant: ssrRestaurant, restaurantId, defaultCollapse }) => {
  const router = useRouter()

  const { data: restaurantData } = useSingleRestaurantQuery({
    variables: { id: restaurantId },
  })

  const restaurant = restaurantData?.restaurant || ssrRestaurant

  const daily = restaurant.reservables?.find(isDaily)
  const isMobile = useMediaQuery({ query: mediaQueryIsMobile })

  const now = useMemo(() => dayjs(), [])

  const { data: reservablesData, loading: reservablesLoading } = useReservablesQuery({
    variables: {
      restaurantId: restaurantId,
      dates: {
        startsOn: dayjsToString(now),
        endsOn: dayjsToString(now.clone().add(RESERVABLES_SEARCH_DAYS_COUNT, 'day')),
      },
    },
  })

  const reservables = reservablesData?.reservables || []

  const isTablet = useMediaQuery({ query: mediaQueryIsTablet })
  const modal = useModal()

  useEffect(() => {
    daily &&
      runEcommerceDetails({
        reservableType: RESERVABLE_TYPES.DAILY,
        reservableData: { restaurant },
      })
  }, [daily])

  const FERReservables = restaurant.reservables?.filter(isFER)

  const hasFER = FERReservables.length > 0
  const hasReservableExtras = restaurant.reservableExtras?.length > 0
  const hasReservablesWithMenus = hasReservableExtras || hasFER

  const noSlotsAvailable = reservables.length === 0 // TODO: umądrzyć

  let slotSelect = <SlotSelectionSkeleton />

  const navigateToCheckout = useCallback(
    (
      reservation:
        | CreateDailyReservationMutation['createDailyReservation']
        | CreateFestivalReservationMutation['createFestivalReservation'],
      isCHMReservation: boolean
    ) => {
      router.push(getInitialCheckoutPath({ reservation: reservation, isCHMReservation, initialPath: router.asPath }))
      modal.close()
    },
    [router]
  )

  const clearSearchParams = useCallback(() => {
    return router.replace({ pathname: router.pathname, query: { slug: router.query.slug } }, undefined, { shallow: true })
  }, [router])

  if (!reservablesLoading) {
    slotSelect = noSlotsAvailable ? (
      <NoSlotsBoard />
    ) : (
      <ModalInnerContainer>
        <SlotsHeading>
          {isMobile && <ModalDescription>{restaurant.name || 'Restaurant'}</ModalDescription>}
          <SlotsTitle>{gt.tp('RestaurantPage', 'Book a table with or without a menu')}</SlotsTitle>
        </SlotsHeading>
        <SlotsContainer>
          <RestaurantReservablesView
            reservables={reservables}
            defaultCollapse={defaultCollapse}
            onReservationCreated={navigateToCheckout}
            onSectionCollapse={clearSearchParams}
            hasReservableExtras={hasReservableExtras}
          />
        </SlotsContainer>
      </ModalInnerContainer>
    )
  }

  const code =
    Array.isArray(FERReservables) && FERReservables.length > 0 && router.query.name
      ? FERReservables.find(item => item.festivalEdition.festival.slug === router.query.name)?.festivalEdition.festival.code || null
      : null

  const { data: pageDetailData } = usePageDetailQuery({
    variables: {
      slug: `listing-${code}`,
    },
    skip: !code,
    fetchPolicy: 'cache-first',
  })

  const openSlotSelectModal = () => {
    modal.open(slotSelectModalProps, <ModalContainer>{slotSelect}</ModalContainer>)
  }

  const sourceParams = {
    mobile: {
      media: '(max-width: 989px)',
      sizes: '(max-width: 731px) 100vw, (max-width: 989px) 700px',
    },
    desktop: {
      media: '(min-width: 990px)',
      sizes: '(max-width: 1280px) calc(100vw - 2rem), 1280px',
    },
  }

  const allFERImages = FERReservables.map(({ imageFiles }) => imageFiles).flat()

  const { tabRefs, activeTab } = useRestaurantPageTabs()

  const tabs = useMemo(() => getRestaurantPageTabs({ gt, hasReservablesWithMenus }), [hasReservablesWithMenus])

  return (
    <BackgroundWrapper>
      <UniversalContainer extraPaddingOnMobile={false}>
        <ProductContainer>
          <ProductGrid>
            <HeaderContainer>
              <ProductPageTitle restaurant={restaurant} />
              <StyledToastContainer id={'FERToastContainer'} />
            </HeaderContainer>
            {hasFER && code && pageDetailData?.pageDetail?.topBanner?.files && (
              <FestivalBannerContainer>
                <FestivalBanner files={pageDetailData?.pageDetail?.topBanner?.files} sourceParams={sourceParams} />
                <OrSeparator>
                  <div>{gt.tp('RestaurantPage', 'or')}</div>
                </OrSeparator>
              </FestivalBannerContainer>
            )}

            <RestaurantPageCarousel restaurantName={restaurant.name} images={[...allFERImages, ...restaurant.imageFiles]} />

            <RestaurantSelectContainer>
              {noSlotsAvailable && !reservablesLoading && <NoSlotsBoard />}
              {!noSlotsAvailable && isTablet ? (
                <Button onClick={openSlotSelectModal} color='orange' width='full' label={gt.tp('RestaurantPage', 'Book a table')} />
              ) : (
                slotSelect
              )}
              <SelectSeparator insideGrid={true} />
            </RestaurantSelectContainer>
            <TabsWrapper>
              <ScrollableTabs activeTab={activeTab?.id} tabs={tabs} />
              <ContentContainer>
                {hasReservablesWithMenus && (
                  <Section id='menu' ref={tabRefs.menuRef}>
                    <MenuTabView restaurant={restaurant} reservables={reservables} onReserveClick={openSlotSelectModal} />
                  </Section>
                )}

                <ProductPageSectionSeparator />
                <ProductPageSection id='description' title='Description' icon='text-align-left' contentRef={tabRefs.descriptionRef}>
                  <Description
                    chef={restaurant.chef || ''}
                    description={restaurant.description || ''}
                    establishmentYear={restaurant.establishmentYear || 0}
                    isMobile={isMobile}
                    tags={restaurant.tags}
                  />
                </ProductPageSection>

                <ProductPageSectionSeparator />
                <ProductPageSection id='features' title='Features' icon='restaurant' contentRef={tabRefs.featuresRef}>
                  <Features tags={restaurant.tags} />
                </ProductPageSection>

                <ProductPageSectionSeparator />
                <ProductPageSection
                  id='location'
                  title='Location and contact details'
                  icon='location-outline'
                  contentRef={tabRefs.locationRef}>
                  <Contact
                    longitude={restaurant.longitude}
                    latitude={restaurant.latitude}
                    address={restaurant.address}
                    phoneNumber={restaurant.phoneNumber}
                    urls={restaurant.urls || []}
                  />
                </ProductPageSection>
              </ContentContainer>
            </TabsWrapper>
          </ProductGrid>
        </ProductContainer>
      </UniversalContainer>
    </BackgroundWrapper>
  )
}

export default RestaurantPage
